import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import mapboxgl from 'mapbox-gl';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { MAPBOX_APIKEY } from '@constants';
import { PageHeader } from '../../tailwindComponents/PageHeader';

mapboxgl.accessToken = MAPBOX_APIKEY;

// Diamond icon for Gainshare
function DiamondIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox='0 0 24 24' fill='currentColor'>
      <path d='M12 2L2 9l10 13 10-13L12 2zm0 3.84L18.26 9 12 18.54 5.74 9 12 5.84z' />
    </svg>
  );
}

export function ShipmentQuote() {
  useDocumentTitle('FLOX - Shipment Quote');
  const navigate = useNavigate();
  const { id } = useParams();

  const pages = [
    { name: 'Shipments', href: '/shipments', current: false },
    { name: 'Outbound shipments', href: '/outbound-shipments', current: false },
    { name: `Quote #${id || '5765-QT'}`, href: '#', current: true },
  ];

  // Mock data
  const quoteData = {
    quoteId: '5765-QT',
    status: 'Confirmed',
    shipmentDetails: {
      purchaseOrderNumber: '453',
      shipmentId: '4345653',
      warehouse: {
        name: 'Warehouse A, 1235684',
        address: 'ABC Logistics, 12 High Street, Manchester, M1 2AB',
      },
      client: {
        name: 'Judith Belington',
        address: 'Food Retailer, 34 Market Road, London, EC1M 6BY',
      },
      rta: '15/06/2025',
      specialInstructions: 'Deliver fast – fresh items.',
    },
    selectedRoute: {
      price: '£280',
      gainshare: '£15',
      rtaPlus: 1,
      status: 'awaiting',
      carrier: {
        name: '3T-Europe Ltd',
        rating: 4.8,
        verified: true,
      },
      pickUp: '10:00 - 10:45',
      dropOffs: [
        { name: 'Tesco North', time: '13:00 - 14:00', warning: false },
        { name: 'Sainsbury DC12', time: '14:00 - 15:00', warning: true },
      ],
    },
    alternativeRoute: {
      price: '£350',
      gainshare: '£8',
      carrier: {
        name: 'ABC Haulage',
        rating: 4.8,
        verified: true,
      },
      pickUp: '12:30 - 13:30',
      dropOffs: [
        { name: 'Tesco North', time: '15:30 - 17:00', warning: false },
        { name: 'Sainsbury DC12', time: '16:30 - 18:00', warning: false },
      ],
    },
  };

  const headerContent = (
    <div className='flex items-center gap-3'>
      <span>Shipment quote</span>
      <span className='px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full'>
        #{quoteData.quoteId}
      </span>
    </div>
  );

  const editButton = (
    <button
      type='button'
      onClick={() => navigate('/outbound-shipment/new')}
      className='px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors'
    >
      Edit Shipment
    </button>
  );

  return (
    <div className='h-[calc(100vh-64px)] flex flex-col bg-gray-50'>
      <PageHeader header={headerContent} pages={pages}>
        {editButton}
      </PageHeader>

      <div className='flex-1 overflow-auto px-10 py-6'>
        <div className='grid grid-cols-2 gap-6'>
          {/* Left Column */}
          <div className='flex flex-col gap-y-6'>
            {/* Shipment details */}
            <div className='bg-white rounded-lg border border-gray-200 p-5'>
              <h3 className='text-lg font-semibold text-gray-900 mb-4'>
                Shipment details
              </h3>

              <div className='flex flex-col gap-y-2'>
                <p className='text-sm'>
                  <span className='font-semibold text-gray-900'>
                    Purchase order #:
                  </span>{' '}
                  <span className='text-gray-900'>
                    {quoteData.shipmentDetails.purchaseOrderNumber}
                  </span>
                </p>
                <p className='text-sm'>
                  <span className='font-semibold text-gray-900'>
                    Shipment ID:
                  </span>{' '}
                  <span className='text-gray-900'>
                    {quoteData.shipmentDetails.shipmentId}
                  </span>
                </p>

                <div className='mt-2'>
                  <p className='text-sm'>
                    <span className='font-semibold text-gray-900'>
                      Warehouse:
                    </span>{' '}
                    <span className='text-gray-900'>
                      {quoteData.shipmentDetails.warehouse.name}
                    </span>
                  </p>
                  <p className='text-sm text-gray-500 flex items-center gap-1 mt-1'>
                    <svg
                      className='w-4 h-4 text-gray-400 flex-shrink-0'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                      />
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                      />
                    </svg>
                    {quoteData.shipmentDetails.warehouse.address}
                  </p>
                </div>

                <div className='mt-2'>
                  <p className='text-sm'>
                    <span className='font-semibold text-gray-900'>Client:</span>{' '}
                    <span className='text-gray-900'>
                      {quoteData.shipmentDetails.client.name}
                    </span>
                  </p>
                  <p className='text-sm text-gray-500 flex items-center gap-1 mt-1'>
                    <svg
                      className='w-4 h-4 text-gray-400 flex-shrink-0'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                      />
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                      />
                    </svg>
                    {quoteData.shipmentDetails.client.address}
                  </p>
                </div>

                <p className='text-sm mt-2'>
                  <span className='font-semibold text-gray-900'>RTA:</span>{' '}
                  <span className='text-gray-900'>
                    {quoteData.shipmentDetails.rta}
                  </span>
                </p>

                <p className='text-sm mt-2'>
                  <span className='font-semibold text-gray-900'>
                    Special instructions:
                  </span>{' '}
                  <span className='text-gray-900'>
                    {quoteData.shipmentDetails.specialInstructions}
                  </span>
                </p>
              </div>
            </div>

            {/* Selected route */}
            <div className='bg-white rounded-lg border border-gray-200 p-5'>
              <div className='flex items-center justify-between mb-2'>
                <h3 className='text-lg font-semibold text-gray-900'>
                  Selected route
                </h3>
                <div className='flex gap-2'>
                  <button
                    type='button'
                    className='p-2 rounded-lg border border-gray-200 hover:bg-gray-50'
                  >
                    <ChevronLeftIcon className='w-4 h-4 text-gray-500' />
                  </button>
                  <button
                    type='button'
                    className='p-2 rounded-lg border border-gray-200 hover:bg-gray-50'
                  >
                    <ChevronRightIcon className='w-4 h-4 text-gray-500' />
                  </button>
                </div>
              </div>
              <p className='text-sm text-gray-500 mb-6'>
                Wait for Carrier response or choose the best alternative option
                instead.
              </p>

              {/* Quote Cards Row */}
              <div className='grid grid-cols-[200px_1fr_1fr] gap-4 mb-4'>
                <div />
                {/* Selected option card */}
                <div className='flex flex-col items-center p-4'>
                  <div className='flex items-center gap-2 mb-2'>
                    <span className='text-primary font-semibold'>Selected</span>
                    <span className='px-2 py-0.5 bg-blue-100 text-primary text-xs font-medium rounded-full'>
                      RTA +{quoteData.selectedRoute.rtaPlus}
                    </span>
                  </div>
                  <div className='flex items-center gap-2 mb-3'>
                    <span className='text-4xl font-bold text-gray-900'>
                      {quoteData.selectedRoute.price}
                    </span>
                    <div className='flex items-center gap-1'>
                      <DiamondIcon className='w-4 h-4 text-primary' />
                      <div className='flex flex-col'>
                        <span className='text-sm font-semibold text-green-600'>
                          {quoteData.selectedRoute.gainshare}
                        </span>
                        <span className='text-xs text-gray-500'>Gainshare</span>
                      </div>
                    </div>
                  </div>
                  <button
                    type='button'
                    className='w-full py-2.5 px-6 bg-orange-50 text-orange-600 font-medium rounded-lg border border-orange-200 text-sm'
                  >
                    Awaiting Confirmation
                  </button>
                </div>
                {/* Alternative option card */}
                <div className='flex flex-col items-center p-4'>
                  <span className='text-gray-900 font-semibold mb-2'>
                    Best Alternative
                  </span>
                  <div className='flex items-center gap-2 mb-3'>
                    <span className='text-4xl font-bold text-gray-900'>
                      {quoteData.alternativeRoute.price}
                    </span>
                    <div className='flex items-center gap-1'>
                      <DiamondIcon className='w-4 h-4 text-primary' />
                      <div className='flex flex-col'>
                        <span className='text-sm font-semibold text-green-600'>
                          {quoteData.alternativeRoute.gainshare}
                        </span>
                        <span className='text-xs text-gray-500'>Gainshare</span>
                      </div>
                    </div>
                  </div>
                  <button
                    type='button'
                    className='w-full py-2.5 px-6 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors text-sm'
                  >
                    Book Now
                  </button>
                </div>
              </div>

              {/* Route details table */}
              <div className='border-t border-gray-200'>
                {/* Pick-Up row */}
                <div className='grid grid-cols-[200px_1fr_1fr] gap-4 py-4 border-b border-gray-100'>
                  <span className='text-sm text-gray-500'>Pick-Up</span>
                  <span className='text-sm text-gray-900 text-center'>
                    {quoteData.selectedRoute.pickUp}
                  </span>
                  <span className='text-sm text-gray-900 text-center'>
                    {quoteData.alternativeRoute.pickUp}
                  </span>
                </div>

                {/* Drop-Off rows */}
                {quoteData.selectedRoute.dropOffs.map((dropOff, index) => (
                  <div
                    key={dropOff.name}
                    className='grid grid-cols-[200px_1fr_1fr] gap-4 py-4 border-b border-gray-100'
                  >
                    <span className='text-sm text-gray-500'>
                      Drop-Off {index + 1}: {dropOff.name}
                    </span>
                    <div className='flex items-center justify-center gap-1'>
                      <span className='text-sm text-gray-900'>
                        {dropOff.time}
                      </span>
                      {dropOff.warning && (
                        <ExclamationCircleIcon className='w-4 h-4 text-amber-500' />
                      )}
                    </div>
                    <span className='text-sm text-gray-900 text-center'>
                      {quoteData.alternativeRoute.dropOffs[index]?.time}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className='flex flex-col gap-4'>
            {/* Summary card */}
            <div className='bg-white rounded-lg border border-gray-200 p-5'>
              <h3 className='text-lg font-semibold text-gray-900 mb-4'>
                Summary
              </h3>

              <div className='flex flex-col gap-3'>
                <div className='flex justify-between'>
                  <span className='text-sm text-gray-500'>Total SKUs</span>
                  <span className='text-sm font-semibold text-gray-900'>3</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-sm text-gray-500'>Total quantity</span>
                  <span className='text-sm font-semibold text-gray-900'>
                    150 units
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-sm text-gray-500'>Total weight</span>
                  <span className='text-sm font-semibold text-gray-900'>
                    249 kg
                  </span>
                </div>
              </div>

              <div className='mt-6'>
                <h4 className='text-base font-semibold text-gray-900 mb-3'>
                  Your Savings
                </h4>
                <div className='flex gap-6'>
                  <div>
                    <div className='flex items-center gap-1'>
                      <span className='text-green-500'>↑</span>
                      <span className='text-lg font-semibold text-green-500'>
                        £21.50
                      </span>
                    </div>
                    <span className='text-xs text-gray-500'>
                      VS next best on FLOX
                    </span>
                  </div>
                  <div>
                    <div className='flex items-center gap-1'>
                      <span className='text-green-500'>↑</span>
                      <span className='text-lg font-semibold text-green-500'>
                        £142.42
                      </span>
                    </div>
                    <span className='text-xs text-gray-500'>
                      VS average market
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Route Map */}
            <RouteMap />
          </div>
        </div>
      </div>
    </div>
  );
}

function RouteMap() {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  // Mock route points
  const routePoints = [
    {
      id: 'pickup',
      label: 'Pick up 1',
      sublabel: 'Warehouse',
      coordinates: [-1.5, 53.5] as [number, number], // Near Leeds
    },
    {
      id: 'dropoff1',
      label: 'Drop-Off 1',
      sublabel: 'Tesco North',
      coordinates: [-2.5, 53.0] as [number, number], // Near Manchester
    },
    {
      id: 'dropoff2',
      label: 'Drop-Off 2',
      sublabel: 'Sainsbury DC12',
      coordinates: [-3.0, 52.5] as [number, number], // Near Birmingham
    },
  ];

  useEffect(() => {
    if (!mapContainerRef.current) return undefined;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [-2.2, 53.0],
      zoom: 7,
      interactive: true,
    });

    mapRef.current = map;

    map.on('load', () => {
      // Add route line
      const routeCoordinates = routePoints.map((p) => p.coordinates);

      map.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: routeCoordinates,
          },
        },
      });

      map.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#2563eb',
          'line-width': 3,
        },
      });

      // Add markers for each point
      routePoints.forEach((point) => {
        const el = document.createElement('div');
        el.className = 'route-marker';
        el.innerHTML = `
          <div style="
            background: white;
            border-radius: 8px;
            padding: 6px 10px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            font-size: 12px;
            white-space: nowrap;
          ">
            <div style="font-weight: 600; color: #2563eb;">${point.label}</div>
            <div style="color: #6b7280; font-size: 11px;">${point.sublabel}</div>
          </div>
        `;

        new mapboxgl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat(point.coordinates)
          .addTo(map);
      });
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  return (
    <div
      ref={mapContainerRef}
      className='w-full rounded-lg overflow-hidden border border-gray-200'
      style={{ height: '600px' }}
    />
  );
}
